import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface CertificateForSign {
  thumbprint: string;
  name: string;
  subjectName: string;
  validFrom: string;
  validTo: string;
}

interface ModalContent {
  data?: string;              // массив? файлов, либо хешей
  title?: string;             // опция, название для окна
  content?: string;           // опция, текст для окна
  confirmAction?: () => void; // TODO: выкинуть сюда формирование подписи
  /** Режим: выбор сертификата (для initKey) или подписание (с file data) */
  mode?: 'select';
}

export const useSignModal = defineStore('modal', () => {
  // Состояние: открыто ли модальное окно
  const isOpen = ref(false);
  // Состояние: динамические данные для отображения в модалке (опционально)
  const modalData = ref<ModalContent | null>(null);
  // Сформированная подпись (массив подписей)
  const signature = ref<string | null>(null);

  /** Promise-резолвер для openForCertificateSelection */
  let certificateSelectionResolve: ((cert: CertificateForSign | null) => void) | null = null;

  function open(data?: string, title?: string, content?: string) {
    isOpen.value = true;
  }

  /**
   * Открывает модальное окно выбора сертификата (для initKey).
   * Возвращает Promise, который резолвится выбранным сертификатом при подтверждении
   * или null при отмене.
   */
  function openForCertificateSelection(): Promise<CertificateForSign | null> {
    modalData.value = { mode: 'select' };
    isOpen.value = true;
    signature.value = null;
    return new Promise<CertificateForSign | null>((resolve) => {
      certificateSelectionResolve = resolve;
    });
  }

  /** Вызывается из Certificates.vue при подтверждении выбора сертификата (режим select) */
  function resolveCertificateSelection(cert: CertificateForSign | null) {
    if (certificateSelectionResolve) {
      certificateSelectionResolve(cert);
      // сбрасываем резолвер
      certificateSelectionResolve = null;
    }
    close();
  }

  function close() {
    if (certificateSelectionResolve) {
      certificateSelectionResolve(null);
      // сбрасываем резолвер
      certificateSelectionResolve = null;
    }
    isOpen.value = false;
    modalData.value = null;
  }

  const confirm = () => {
    if (modalData.value?.confirmAction) {
      modalData.value.confirmAction();
    }
    close();
  };

  function setData(data: string) {
    modalData.value = { ...modalData.value, data };
  }

  function getSignature() {
    return signature;
  }

  function setSignature(sign: string) {
    signature.value = sign;
  }

  return {
    isOpen,
    modalData,
    openModal: open,
    closeModal: close,
    confirmModal: confirm,
    openForCertificateSelection,
    resolveCertificateSelection,
    setData,
    getSignature,
    setSignature,
  };
});
